import React, { useCallback, useEffect, useState } from "react";
import { fetchTcsList } from "../../../../shared/data/api/tcsApi";
import { fetchTcsSections, TaxLedgerMapEntry } from "../../../../services/api";
import type { TcsDTO } from "../../../../shared/data/models/TaxDTOs";
import { TcsRateForm } from "./TcsRateForm";
import { AddRateButton, RateRowMenu } from "./rateTableParts";
import {
  FRESH_ROW_CLASS,
  isExpiredFlag,
  TCS_COLUMNS_LEDGER,
} from "./tdsTable";

/**
 * The `tcs` table's cap. The backend refuses a limit above 100 and a real org has
 * a couple of dozen collection codes, so one page is the whole set — no
 * pagination control that would only ever render "1 of 1".
 */
const PAGE_SIZE = 100;

/** Which form this panel has open. One at a time — see the TDS panel's note. */
type OpenForm = { mode: "add" } | { mode: "edit"; rate: TcsDTO };

export interface TcsRatesListProps {
  /** Bumped by the page after a Zoho sync lands. */
  reloadToken?: number;
  /** tax_id → the account that rate's tax lands in. Null means "not read". */
  ledgerMap?: Record<string, TaxLedgerMapEntry> | null;
  onCounts?: (counts: { active: number; expired: number }) => void;
  onZohoWrite?: () => void;
}

/**
 * TCS rates in the same table language as the TDS panel above it.
 *
 * Add, sync, and edit. The edit is a ZOHO write, not a local one — `tcs` still
 * carries nothing a tenant curates, so the earlier "no edit" reasoning was about
 * a form that wrote to `tcs` and would have lost on the next sync. This one
 * writes name, rate and nature of collection to Zoho and mirrors the answer back,
 * which keeps Zoho the source of truth exactly as Add does.
 *
 * NO EDIT ON AN EXPIRED ROW. Zoho refuses to change a rate from the retired era
 * and the endpoint says so with a 422; an action that can only fail is worse than
 * no action, so the menu item is listed and DISABLED rather than dropped — a row
 * whose menu is empty reads as a row that has been forgotten about. This is where
 * the TCS table differs from the TDS one, whose Edit only touches locally-curated
 * columns and so works on any row.
 */
export const TcsRatesList: React.FC<TcsRatesListProps> = ({
  reloadToken = 0,
  ledgerMap = null,
  onCounts,
  onZohoWrite,
}) => {
  const [rows, setRows] = useState<TcsDTO[]>([]);
  const [openForm, setOpenForm] = useState<OpenForm | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [freshId, setFreshId] = useState<string | null>(null);
  /**
   * Whether the nature-of-collection list is in. TCS has no seeded catalogue, so
   * this is a live Zoho read and the add button waits on it the same way the TDS
   * one waits on its own — the form's first field is a nature of collection, and
   * Zoho matches on nothing else.
   */
  const [sectionsLoaded, setSectionsLoaded] = useState(false);
  const [sectionError, setSectionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchTcsList(undefined, PAGE_SIZE, 0);
      setRows(response.data.tcs);
      setTotal(response.data.pagination?.total ?? response.data.tcs.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load TCS rates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  useEffect(() => {
    let live = true;
    fetchTcsSections()
      .then(() => {
        if (live) setSectionsLoaded(true);
      })
      .catch((err) => {
        if (live) {
          setSectionError(
            err instanceof Error
              ? `Could not read the section list: ${err.message}`
              : "Could not read the section list.",
          );
        }
      });
    return () => {
      live = false;
    };
  }, []);

  const activeCount = rows.filter((r) => !isExpiredFlag(r.is_expired)).length;

  useEffect(() => {
    onCounts?.({ active: activeCount, expired: rows.length - activeCount });
  }, [onCounts, activeCount, rows.length]);

  const hidden = Math.max(0, total - rows.length);

  const handleSaved = (message: string, writtenTaxId: string | null) => {
    setNotice(message);
    setFreshId(writtenTaxId);
    setOpenForm(null);
    load();
    onZohoWrite?.();
  };

  return (
    <div className="flex w-full flex-col items-start gap-3">
      {notice && (
        <div className="flex w-full items-start rounded-lg border border-solid border-green-200 bg-green-50 p-3">
          <span className="text-body font-body text-green-800">{notice}</span>
        </div>
      )}

      {error && (
        <div className="flex w-full items-start rounded-lg border border-solid border-red-200 bg-red-50 p-3">
          <span className="text-body font-body text-red-600">{error}</span>
        </div>
      )}

      {rows.length === 0 ? (
        <span className="text-body font-body text-subtext-color">
          {loading
            ? "Loading…"
            : "No TCS rates found. They arrive with the Zoho tax sync."}
        </span>
      ) : (
        <div className="flex w-full flex-col">
          <div
            className={`hidden gap-x-4 border-b border-solid border-neutral-border pb-1.5 text-caption-bold font-caption-bold uppercase text-subtext-color sm:grid ${TCS_COLUMNS_LEDGER}`}
          >
            <span>Nature of collection</span>
            <span>Tax name</span>
            <span className="text-right">Rate</span>
            <span>Payable ledger</span>
            <span>Status</span>
            <span />
          </div>

          {rows.map((row) => {
            const expired = isExpiredFlag(row.is_expired);
            const payable = ledgerMap?.[row.tcs_id]?.payable_account_name ?? null;
            const fresh = freshId !== null && row.tcs_id === freshId;

            return (
              <div key={row.id} className={fresh ? FRESH_ROW_CLASS : undefined}>
                <div
                  className={`grid items-baseline gap-x-4 gap-y-1 border-b border-solid border-neutral-border py-2.5 ${
                    fresh ? "border-b-0 px-2" : ""
                  } ${TCS_COLUMNS_LEDGER}`}
                >
                  <span className="min-w-0 break-words text-body font-body text-default-font">
                    {row.nature_of_collection || "—"}
                  </span>
                  <span className="min-w-0 break-words text-body font-body text-subtext-color">
                    {row.tax_name}
                  </span>
                  <span className="text-body font-body text-default-font sm:text-right">
                    {row.tax_rate}%
                  </span>
                  <span
                    className={`min-w-0 break-words text-body font-body ${
                      payable === null
                        ? "text-subtext-color"
                        : "text-default-font"
                    }`}
                  >
                    {payable ?? "—"}
                  </span>
                  <span
                    className={`text-body font-body ${
                      expired ? "text-red-600" : "text-subtext-color"
                    }`}
                  >
                    {expired ? "Expired" : "Active"}
                  </span>
                  <RateRowMenu
                    items={[
                      {
                        label: "Edit",
                        // Zoho refuses to change a rate from the retired era.
                        disabled: expired || !sectionsLoaded,
                        onSelect: () => setOpenForm({ mode: "edit", rate: row }),
                      },
                    ]}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hidden > 0 && (
        <span className="text-caption font-caption text-subtext-color">
          Showing the first {rows.length} of {total}.
        </span>
      )}

      {openForm === null ? (
        <AddRateButton
          label="TCS"
          loaded={sectionsLoaded}
          error={sectionError}
          onOpen={() => setOpenForm({ mode: "add" })}
        />
      ) : (
        <div className="flex w-full flex-col border-t border-solid border-neutral-border pt-3">
          <TcsRateForm
            key={openForm.mode === "edit" ? openForm.rate.id : "add"}
            rate={openForm.mode === "edit" ? openForm.rate : null}
            onSaved={handleSaved}
            onCancel={() => setOpenForm(null)}
          />
        </div>
      )}
    </div>
  );
};

export default TcsRatesList;
